export interface SiteInvite {
  /**
   * Invite ID.
   * @format GUID
   * @readonly
   */
  id?: string;
  /**
   * Site ID the user is invited to as a collaborator.
   * @format GUID
   * @readonly
   */
  siteId?: string;
  /**
   * Email address where the invite was sent.
   * @format EMAIL
   */
  email?: string;
  /** Role IDs included in the invite. */
  policyIds?: string[];
  /**
   * Deprecated. Use `inviterAccountId`.
   * @readonly
   * @deprecated
   */
  inviterId?: string;
  /**
   * Invite Status.
   *
   * Supported values:
   * - **Pending:** The invite has been sent and is valid, waiting for the user's response.
   * - **Used:** The invite has been accepted.
   * - **Deleted:** The invite has been deleted or revoked.
   * - **Declined:** The user declined the invite.
   * - **Expired:** The invite has expired without being accepted.
   */
  status?: InviteStatusWithLiterals;
  /** Link to accept the invite. */
  acceptLink?: string;
  /**
   * Inviting account ID.
   * @format GUID
   * @readonly
   */
  inviterAccountId?: string;
  /**
   * Account ID that accepted the invite. Populated only once the invite is accepted.
   * @format GUID
   * @readonly
   */
  acceptedByAccountId?: string | null;
  /** Date the invite was created. */
  dateCreated?: Date | null;
  /**
   * User's Wix Bookings staff ID, if relevant.
   * @format GUID
   */
  staffId?: string | null;
  /** Invite expiration date */
  expirationDate?: Date | null;
  /**
   * Location ids included in the invite
   * @format GUID
   * @readonly
   * @maxSize 20
   */
  locationIds?: string[];
  /**
   * A set of capability keys representing the actions that the caller is allowed
   * to perform on this specific contributor. This field is calculated based on the
   * identity of the request sender and may differ between callers.
   * @maxSize 20
   * @maxLength 20
   */
  callerCapabilities?: string[];
}

/** Invite status stating whether the invite was accepted, waiting to be accepted, deleted etc.. */
export enum InviteStatus {
  Pending = 'Pending',
  Used = 'Used',
  Deleted = 'Deleted',
  Declined = 'Declined',
  Expired = 'Expired',
}

/** @enumType */
export type InviteStatusWithLiterals =
  | InviteStatus
  | 'Pending'
  | 'Used'
  | 'Deleted'
  | 'Declined'
  | 'Expired';

export interface GetSiteInvitesRequest {}

export interface GetSiteInvitesResponse {
  invites?: SiteInvite[];
}

export interface QuerySiteInvitesRequest {
  /**
   * Supports only `filter` field with
   * `"filter" : {
   * "acceptedByAccountId":{"$in": [<id1>, <id2>, ...]}
   * }`
   */
  query?: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   */
  sort?: Sorting[];
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QuerySiteInvitesResponse {
  invites?: SiteInvite[];
}

export interface GetSiteInviteRequest {
  id?: string;
}

export interface GetSiteInviteResponse {
  invite?: SiteInvite;
}

export interface SiteInviteRequest {
  /** The role ids to be assigned */
  policyIds?: string[];
  /**
   * Invitee email
   * @format EMAIL
   */
  email?: string;
  /** The language of emails that will be used only for recipients that don't have a user, in case this parameter is unspecified, the sender's language will be used instead */
  defaultEmailLanguage?: string | null;
  /** Location restriction for the invite. */
  locationRestriction?: LocationRestriction;
}

export interface LocationRestriction {
  /**
   * @format GUID
   * @maxSize 20
   */
  locationIds?: string[];
}

export interface SiteInviteResponse {
  /** Invites that were sent. */
  invite?: SiteInvite;
}

export interface BulkSiteInviteRequest {
  /** Role IDs, referred to as policy IDs, to assign to the contributors. */
  policyIds: string[];
  /**
   * Email addresses to which the invites should be sent.
   * @minSize 1
   * @maxSize 10
   */
  emails: string[];
  /** Details explaining the purpose of the invite. */
  invitePurpose?: string | null;
  /** Language of emails to send. Relevant only for recipients that don't currently have a Wix user ID. Default: Site owner's language. */
  defaultEmailLanguage?: string | null;
  /** Location restriction for the invite. */
  locationRestriction?: LocationRestriction;
}

export interface BulkSiteInviteResponse {
  /** Invites that were sent successfully. */
  invites?: SiteInvite[];
  /** Invites that failed. */
  failedEmails?: string[];
}

export interface ResendSiteInviteRequest {
  /** Invite ID. */
  inviteId: string;
  /** Language of emails to send. Relevant only for recipients that don't currently have a Wix user ID. Default: Site owner's language. */
  defaultEmailLanguage?: string | null;
}

export interface AcceptSiteInviteRequest {
  inviteToken?: string;
}

export interface AcceptSiteInviteResponse {}

export interface RevokeSiteInviteRequest {
  /** Invite ID. */
  inviteId: string;
}

export interface RevokeSiteInviteResponse {}

export interface UpdateSiteInviteRequest {
  inviteId?: string;
  policyIds?: string[];
  /** @format GUID */
  staffId?: string | null;
  locationRestriction?: LocationRestriction;
}

export interface UpdateSiteInviteResponse {}

export interface GetContributorLimitRequest {}

export interface GetContributorLimitResponse {
  contributorLimitation?: ContributorLimitation;
}

export interface ContributorLimitation {
  contributorLimit?: number;
  leftInvites?: number;
}

export interface ParseSiteInviteTokenRequest {
  inviteToken?: string;
}

export interface ParseSiteInviteTokenResponse {
  inviteId?: string;
  siteId?: string;
  status?: InviteStatusWithLiterals;
}
